import type { GetLocationResponse } from "@/openapi/generated/models";
import { PageTypeGlobal } from "@/lib/types";
import { formatLocationAddressSingleLine } from "@/lib/utils/formatLocationAddress";
import { buildLocationPublicPath } from "@/lib/utils/publicPaths";

export function getActivePublicLocations(
  locations: GetLocationResponse[] | undefined,
): GetLocationResponse[] {
  return (locations ?? [])
    .filter((location) => location.isActive !== false)
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" }),
    );
}

/** First active location with a postal address, otherwise the first active location. */
export function pickPrimaryPublicLocation(
  locations: GetLocationResponse[] | undefined,
): GetLocationResponse | null {
  const active = getActivePublicLocations(locations);
  if (active.length === 0) return null;

  return (
    active.find(
      (location) => formatLocationAddressSingleLine(location).length > 0,
    ) ?? active[0]
  );
}

export function hasMultiplePublicLocations(
  locations: GetLocationResponse[] | undefined,
): boolean {
  return getActivePublicLocations(locations).length > 1;
}

export type PublicLocationsNavLink = {
  href: string;
  label: "Location" | "Locations";
};

/** Public navbar: hide when none; singular link to detail when one; listing when many. */
export function resolvePublicLocationsNavLink(
  locations: GetLocationResponse[] | undefined,
): PublicLocationsNavLink | null {
  const active = getActivePublicLocations(locations);
  if (active.length === 0) return null;

  if (active.length === 1) {
    const location = active[0];
    return {
      href: buildLocationPublicPath({
        name: location.name || "location",
        locationId: Number(location.locationId),
      }),
      label: "Location",
    };
  }

  return {
    href: PageTypeGlobal.Locations,
    label: "Locations",
  };
}
